import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ExternalLink } from 'lucide-react';
import { COMPANIES, ROUTE_PATHS, companyPath } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
  activeCompanySlug?: string;
}

export default function Layout({ children, activeCompanySlug }: LayoutProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const activeCompany = activeCompanySlug
    ? COMPANIES.find(c => c.slug === activeCompanySlug)
    : null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = activeCompanySlug
    ? [
        { label: 'About', to: companyPath(activeCompanySlug) + '/about' },
        { label: 'Services', to: companyPath(activeCompanySlug) + '/services' },
        ...(activeCompanySlug === 'sequel-advertising'
          ? [{ label: 'Events', to: ROUTE_PATHS.EVENTS }]
          : []),
        { label: 'Portfolio', to: companyPath(activeCompanySlug) + '/portfolio' },
        { label: 'Contact', to: companyPath(activeCompanySlug) + '/contact' },
      ]
    : [
        { label: 'About', to: ROUTE_PATHS.ABOUT },
        { label: 'News', to: ROUTE_PATHS.NEWS },
        { label: 'Talents', to: ROUTE_PATHS.TALENTS },
        { label: 'Contact', to: ROUTE_PATHS.CONTACT },
      ];

  return (
    <div className="min-h-screen bg-[#080808] text-[#f5f5f5]">
      {/* ── Navigation ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'nav-glass' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">

          {/* Logo */}
          <Link
            to={activeCompanySlug ? companyPath(activeCompanySlug) : ROUTE_PATHS.HOME}
            className="flex items-center gap-3 group"
            onClick={() => setMobileOpen(false)}
          >
            <div
              className="w-9 h-9 flex items-center justify-center border text-[10px] font-bold tracking-widest transition-all duration-300 group-hover:border-[#c9a84c]"
              style={{
                borderColor: 'rgba(201,168,76,0.5)',
                color: '#c9a84c',
                fontFamily: 'var(--font-sans)',
              }}
            >
              {activeCompany?.logoInitials ?? 'APC'}
            </div>
            <div className="flex flex-col leading-none">
              <span
                className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#f5f5f5] transition-colors duration-300 group-hover:text-[#c9a84c]"
              >
                {activeCompany?.shortName ?? 'ALV Pageant Circle'}
              </span>
              {!activeCompany && (
                <span className="text-[9px] tracking-[0.15em] uppercase text-[#c9a84c]/70 mt-0.5">
                  Ecosystem
                </span>
              )}
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `text-[11px] tracking-[0.18em] uppercase font-medium transition-all duration-300 relative group ${
                    isActive ? 'text-[#c9a84c]' : 'text-[#f5f5f5]/70 hover:text-[#f5f5f5]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute -bottom-1 left-0 h-[1px] bg-[#c9a84c] transition-all duration-300 ${
                        isActive ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right: Company Switcher + Mobile Hamburger */}
          <div className="flex items-center gap-4">
            {/* Company Switcher */}
            <div ref={switcherRef} className="relative hidden lg:block">
              <button
                onClick={() => setSwitcherOpen(!switcherOpen)}
                className="flex items-center gap-2 px-3 py-1.5 border border-[rgba(201,168,76,0.3)] text-[10px] tracking-[0.18em] uppercase text-[#c9a84c]/80 hover:text-[#c9a84c] hover:border-[rgba(201,168,76,0.6)] transition-all duration-300"
              >
                <span>
                  {activeCompany ? `Viewing: ${activeCompany.shortName}` : 'Explore Companies'}
                </span>
                <ChevronDown
                  size={10}
                  className={`transition-transform duration-300 ${switcherOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {switcherOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-full mt-2 w-64 border border-[rgba(201,168,76,0.2)] bg-[#0e0e0e] shadow-2xl z-50"
                  >
                    <div className="p-3 border-b border-[rgba(201,168,76,0.1)]">
                      <p className="text-[9px] tracking-[0.25em] uppercase text-[#c9a84c]/50">
                        ALV Ecosystem
                      </p>
                    </div>
                    <div className="py-2">
                      <button
                        onClick={() => {
                          navigate(ROUTE_PATHS.HOME);
                          setSwitcherOpen(false);
                        }}
                        className="w-full text-left px-4 py-2.5 flex items-center justify-between group hover:bg-[rgba(201,168,76,0.05)] transition-colors duration-200"
                      >
                        <span className="text-[11px] tracking-wider text-[#f5f5f5]/80 group-hover:text-[#f5f5f5]">
                          Main Hub
                        </span>
                        <ExternalLink size={10} className="text-[#c9a84c]/40 group-hover:text-[#c9a84c]" />
                      </button>
                      {COMPANIES.map(company => (
                        <button
                          key={company.id}
                          onClick={() => {
                            navigate(companyPath(company.slug));
                            setSwitcherOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 flex items-center justify-between group hover:bg-[rgba(201,168,76,0.05)] transition-colors duration-200 ${
                            activeCompanySlug === company.slug ? 'bg-[rgba(201,168,76,0.06)]' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <span
                              className="text-[8px] font-bold tracking-wider border border-[rgba(201,168,76,0.3)] px-1 py-0.5 text-[#c9a84c]/70"
                            >
                              {company.logoInitials}
                            </span>
                            <span className="text-[11px] tracking-wider text-[#f5f5f5]/80 group-hover:text-[#f5f5f5]">
                              {company.shortName}
                            </span>
                          </div>
                          {activeCompanySlug === company.slug && (
                            <div className="w-1 h-1 rounded-full bg-[#c9a84c]" />
                          )}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center text-[#f5f5f5]/80 hover:text-[#c9a84c] transition-colors duration-300"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile Menu ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-[#060606] flex flex-col pt-20 overflow-y-auto"
          >
            <div className="flex flex-col flex-1 px-8 py-10 gap-2">
              {/* Company badge */}
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-8 h-8 flex items-center justify-center border text-[9px] font-bold tracking-widest"
                  style={{ borderColor: 'rgba(201,168,76,0.5)', color: '#c9a84c', fontFamily: 'var(--font-sans)' }}
                >
                  {activeCompany?.logoInitials ?? 'APC'}
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-[#f5f5f5]">
                    {activeCompany?.shortName ?? 'ALV Pageant Circle'}
                  </p>
                  <p className="text-[9px] tracking-wider text-[#c9a84c]/60 uppercase mt-0.5">
                    {activeCompany ? 'Company Site' : 'Ecosystem Hub'}
                  </p>
                </div>
              </div>

              {/* Nav Links */}
              <div className="border-t border-[rgba(201,168,76,0.1)] pt-6">
                <p className="text-[9px] tracking-[0.25em] uppercase text-[#c9a84c]/40 mb-4">Navigation</p>
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}
                  >
                    <NavLink
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `block py-3.5 text-2xl tracking-wide border-b border-[rgba(255,255,255,0.04)] transition-colors duration-200 ${
                          isActive
                            ? 'text-[#c9a84c]'
                            : 'text-[#f5f5f5]/80 hover:text-[#f5f5f5]'
                        }`
                      }
                      style={{ fontFamily: 'var(--font-heading)', fontWeight: 300 }}
                    >
                      {link.label}
                    </NavLink>
                  </motion.div>
                ))}
              </div>

              {/* Explore Companies */}
              <div className="mt-8 border-t border-[rgba(201,168,76,0.1)] pt-6">
                <p className="text-[9px] tracking-[0.25em] uppercase text-[#c9a84c]/40 mb-4">
                  Explore Companies
                </p>
                {!activeCompanySlug && (
                  <Link
                    to={ROUTE_PATHS.COMPANIES}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2.5 text-sm tracking-[0.15em] uppercase text-[#f5f5f5]/60 hover:text-[#c9a84c] transition-colors duration-200"
                  >
                    View All Companies →
                  </Link>
                )}
                {COMPANIES.map((company, i) => (
                  <motion.div
                    key={company.id}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.05 }}
                  >
                    <Link
                      to={companyPath(company.slug)}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 py-2.5 border-b border-[rgba(255,255,255,0.04)] transition-colors duration-200 ${
                        activeCompanySlug === company.slug
                          ? 'text-[#c9a84c]'
                          : 'text-[#f5f5f5]/60 hover:text-[#f5f5f5]'
                      }`}
                    >
                      <span
                        className="text-[8px] font-bold tracking-wider border border-[rgba(201,168,76,0.3)] px-1 py-0.5 text-[#c9a84c]/70"
                      >
                        {company.logoInitials}
                      </span>
                      <span className="text-sm tracking-[0.1em]">{company.name}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Page Content ── */}
      <main>{children}</main>

      {/* ── Footer ── */}
      <footer className="bg-[#050505] border-t border-[rgba(201,168,76,0.12)] pt-16 pb-8">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">

            {/* Brand col */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-9 h-9 flex items-center justify-center border text-[10px] font-bold tracking-widest"
                  style={{ borderColor: 'rgba(201,168,76,0.5)', color: '#c9a84c', fontFamily: 'var(--font-sans)' }}
                >
                  APC
                </div>
                <div>
                  <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#f5f5f5]">ALV Pageant Circle</p>
                  <p className="text-[9px] tracking-[0.15em] uppercase text-[#c9a84c]/60">Global Talent Ecosystem</p>
                </div>
              </div>
              <p className="text-[#f5f5f5]/40 text-sm leading-relaxed mb-6">
                Where talent meets opportunity. A vertically integrated ecosystem for exceptional individuals seeking world-stage recognition.
              </p>
              <div className="divider-gold-left" />
            </div>

            {/* Companies col */}
            <div>
              <p className="text-[9px] tracking-[0.25em] uppercase text-[#c9a84c]/60 mb-5">Our Companies</p>
              <ul className="space-y-2.5">
                {COMPANIES.map(c => (
                  <li key={c.id}>
                    <Link
                      to={companyPath(c.slug)}
                      className="text-[11px] tracking-[0.12em] text-[#f5f5f5]/50 hover:text-[#c9a84c] transition-colors duration-200 uppercase"
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hub nav col */}
            <div>
              <p className="text-[9px] tracking-[0.25em] uppercase text-[#c9a84c]/60 mb-5">Quick Links</p>
              <ul className="space-y-2.5">
                {[
                  { label: 'Home', to: ROUTE_PATHS.HOME },
                  { label: 'About', to: ROUTE_PATHS.ABOUT },
                  { label: 'Ecosystem', to: ROUTE_PATHS.ECOSYSTEM },
                  { label: 'Talents', to: ROUTE_PATHS.TALENTS },
                  { label: 'Events & News', to: ROUTE_PATHS.EVENTS },
                  { label: 'Contact', to: ROUTE_PATHS.CONTACT },
                ].map(link => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-[11px] tracking-[0.12em] text-[#f5f5f5]/50 hover:text-[#c9a84c] transition-colors duration-200 uppercase"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact col */}
            <div>
              <p className="text-[9px] tracking-[0.25em] uppercase text-[#c9a84c]/60 mb-5">Connect</p>
              <div className="space-y-3">
                <a
                  href="mailto:hello@alvpageantcircle.com"
                  className="block text-[11px] tracking-[0.1em] text-[#f5f5f5]/50 hover:text-[#c9a84c] transition-colors duration-200"
                >
                  hello@alvpageantcircle.com
                </a>
                <p className="text-[11px] tracking-[0.1em] text-[#f5f5f5]/40">
                  BGC, Taguig City<br />Metro Manila, Philippines
                </p>
              </div>

              <div className="mt-6">
                <p className="text-[9px] tracking-[0.25em] uppercase text-[#c9a84c]/40 mb-3">Back to Hub</p>
                <Link
                  to={ROUTE_PATHS.HOME}
                  className="btn-gold-outline inline-block text-center"
                  style={{ padding: '0.6rem 1.5rem', fontSize: '0.65rem' }}
                >
                  Main Hub
                </Link>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-[rgba(201,168,76,0.08)] pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#f5f5f5]/25">
              © 2026 ALV Pageant Circle. All rights reserved.
            </p>
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#f5f5f5]/25">
              Where Talent Meets Opportunity
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
